import type {
  GLTFLoaderPlugin,
  GLTFParser,
} from 'three/examples/jsm/loaders/GLTFLoader.js'

/**
 * 在 GLTFLoader 创建 Object3D 前投影 complete GLB 的单根业务 scene。
 *
 * 插件只改变 parser 当前会话内的 scene 入口和选中根位姿，不删除、不重排任何
 * node、mesh、accessor 或 bufferView definition。后续 Object3D 与共享 Geometry、
 * Material、Texture 仍全部由 GLTFLoader 原生创建。
 */
export class CompleteGlbSceneProjectionPlugin implements GLTFLoaderPlugin {
  /** GLTFLoader 插件注册名。 */
  public readonly name = 'PUPPYCAD_complete_glb_scene_projection'

  private readonly parser: GLTFParser

  private readonly selection: PuppyCAD.CompleteGlbRootNodeSelection

  /**
   * 创建单根 scene 投影插件。
   *
   * @param parser 当前 GLTFLoader 解析器。
   * @param selection 准备阶段已经验证的根 node 选择结果。
   */
  public constructor(
    parser: GLTFParser,
    selection: PuppyCAD.CompleteGlbRootNodeSelection,
  ) {
    this.parser = parser
    this.selection = selection
  }

  /**
   * 在 scene/animation/camera 依赖展开前建立单根投影视图。
   */
  public beforeRoot(): null {
    const json = this.parser.json as PuppyCAD.CompleteGlbJsonDocument
    const animations = json.animations
    if (Array.isArray(animations) && animations.length > 0) {
      throw new Error(`完整 GLB 单根投影暂不支持 animation：count=${animations.length}`)
    }

    const selection = this.selection
    const sourceScene = json.scenes?.[selection.sceneIndex]
    const selectedNode = json.nodes?.[selection.nodeIndex]
    if (!sourceScene || !selectedNode) {
      throw new Error(
        `完整 GLB 单根投影缺少选择结果：sceneIndex=${selection.sceneIndex}, nodeIndex=${selection.nodeIndex}`,
      )
    }

    this.assertNodeBelongsToScene(json, selection.sceneIndex, selection.nodeIndex)
    this.assertStaticCadSubtree(json, selection.nodeIndex)

    /**
     * 中间 node 独立成为模型根时，其原 local transform 属于旧父装配的放置语义。
     * 删除 matrix/TRS 后由 glTF 默认值得到单位矩阵，后代 local transform 保持原样。
     */
    delete selectedNode.matrix
    delete selectedNode.translation
    delete selectedNode.rotation
    delete selectedNode.scale

    /**
     * GLTFLoader 会加载 json.scenes 中的全部 scene，而不只加载 json.scene。
     * 因此必须把列表本身投影为单 scene，才能避免其它根 node 创建运行时实例。
     */
    json.scenes = [{
      ...sourceScene,
      nodes: [selection.nodeIndex],
    }]
    json.scene = 0
    return null
  }

  /**
   * 确认策略返回的 node 原本属于对应 scene 的可达树。
   *
   * locator 可以选择 scene 中间层 node，但不能把未挂入该 scene 的孤立 definition
   * 伪装成项目模型，否则根选择错误会被隐藏成一个看似可加载的空上下文模型。
   *
   * @param json 当前 glTF JSON。
   * @param sceneIndex 策略声明的原 scene 索引。
   * @param selectedNodeIndex 策略选中的 node 索引。
   */
  private assertNodeBelongsToScene(
    json: PuppyCAD.CompleteGlbJsonDocument,
    sceneIndex: number,
    selectedNodeIndex: number,
  ): void {
    const nodes = json.nodes ?? []
    const pendingNodeIndices = [...(json.scenes?.[sceneIndex]?.nodes ?? [])]
    const visitedNodeIndices = new Set<number>()

    while (pendingNodeIndices.length > 0) {
      const nodeIndex = pendingNodeIndices.pop()
      if (nodeIndex === undefined || visitedNodeIndices.has(nodeIndex)) {
        continue
      }
      if (nodeIndex === selectedNodeIndex) {
        return
      }
      const node = nodes[nodeIndex]
      if (!node) {
        throw new Error(
          `完整 GLB 原 scene 引用不存在的 node：sceneIndex=${sceneIndex}, nodeIndex=${nodeIndex}`,
        )
      }
      visitedNodeIndices.add(nodeIndex)
      pendingNodeIndices.push(...(node.children ?? []))
    }

    throw new Error(
      `完整 GLB 根选择结果不属于原 scene：sceneIndex=${sceneIndex}, nodeIndex=${selectedNodeIndex}`,
    )
  }

  /**
   * 校验选中子树不会通过 skin 隐式实例化子树外关节 node。
   *
   * @param json 当前 glTF JSON。
   * @param rootNodeIndex 选中的新根 node 索引。
   */
  private assertStaticCadSubtree(
    json: PuppyCAD.CompleteGlbJsonDocument,
    rootNodeIndex: number,
  ): void {
    const nodes = json.nodes ?? []
    const pendingNodeIndices = [rootNodeIndex]
    const visitedNodeIndices = new Set<number>()

    while (pendingNodeIndices.length > 0) {
      const nodeIndex = pendingNodeIndices.pop()
      if (nodeIndex === undefined || visitedNodeIndices.has(nodeIndex)) {
        continue
      }
      const node = nodes[nodeIndex]
      if (!node) {
        throw new Error(`完整 GLB 单根投影引用不存在的 node：nodeIndex=${nodeIndex}`)
      }
      visitedNodeIndices.add(nodeIndex)

      if (node.skin !== undefined) {
        throw new Error(
          `完整 GLB 单根投影暂不支持 skin：nodeIndex=${nodeIndex}, skinIndex=${String(node.skin)}`,
        )
      }

      for (const childNodeIndex of node.children ?? []) {
        if (!Number.isInteger(childNodeIndex) || childNodeIndex < 0 || childNodeIndex >= nodes.length) {
          throw new Error(
            `完整 GLB 单根投影引用非法子 node：nodeIndex=${nodeIndex}, childNodeIndex=${String(childNodeIndex)}`,
          )
        }
        pendingNodeIndices.push(childNodeIndex)
      }
    }
  }
}
